import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";

const CHUNK_SIZE = 500 * 1024;       // 500KB por chunk — nunca excede limites do Cloudflare
const SINGLE_UPLOAD_LIMIT = 1024 * 1024; // Ficheiros ≤ 1MB: pedido único

function readAsBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target.result;
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Upload de ficheiro para MongoDB GridFS.
 * Ficheiros ≤ 1MB: pedido único (rápido).
 * Ficheiros > 1MB: fragmentado em chunks de 500KB (evita timeouts do Cloudflare).
 * @param {File} file
 * @param {function} onProgress - callback(0-100)
 * @returns {{ file_url, file_name, file_size }}
 */
export async function chunkedUpload(file, onProgress) {
  if (!file) return null;

  const report = (pct) => onProgress && onProgress(pct);

  // Ficheiros pequenos (≤1MB): pedido único
  if (file.size <= SINGLE_UPLOAD_LIMIT) {
    const base64 = await readAsBase64(file);
    report(40);
    const { data } = await axios.post(
      `${API}/upload`,
      { filename: file.name, content_type: file.type || "application/octet-stream", data: base64 },
      { headers: { ...getAuthHeader(), "Content-Type": "application/json" } }
    );
    report(100);
    return data;
  }

  // Ficheiros grandes: upload fragmentado
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // 1. Iniciar sessão
  const { data: { upload_id } } = await axios.post(
    `${API}/upload/init`,
    { filename: file.name, content_type: file.type || "application/octet-stream", total_chunks: totalChunks },
    { headers: { ...getAuthHeader(), "Content-Type": "application/json" } }
  );

  // 2. Enviar cada chunk
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const base64 = await readAsBase64(file.slice(start, end));
    await axios.post(
      `${API}/upload/chunk/${upload_id}`,
      { chunk_index: i, data: base64 },
      { headers: { ...getAuthHeader(), "Content-Type": "application/json" } }
    );
    report(Math.round(((i + 1) / totalChunks) * 85));
  }

  // 3. Completar upload — combinar chunks e guardar no GridFS
  const { data } = await axios.post(
    `${API}/upload/complete/${upload_id}`,
    {},
    { headers: { ...getAuthHeader(), "Content-Type": "application/json" } }
  );
  report(100);
  return data;
}
